/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import * as React from "react";
import CheckIcon from "../../icons/Check";
import { cn } from "../../../helpers";
import styles from "./FormCheckbox.module.scss";

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
    /** Checkbox label text */
    label?: string;
    /** Checkbox label text extra styling */
    labelClass?: string;
    /* additional styles */
    wrapperClass?: string;
    /** Checkbox name for grouping checkbox inputs */
    name?: string;
    /** The id of the checkbox */
    id?: string;
    /** Whether the checkbox is in a form */
    inForm?: boolean;
    /** Checkbox description text */
    description?: string;
    /** Remove border for view-only usage */
    noBorder?: boolean;
    /** Additional styles for the input */
    inputClass?: string;
    /** Additional styles for the checkmark */
    checkmarkClass?: string;
    /** Variant styling - 'default' or 'small' */
    variant?: "default" | "small";
}

const FormCheckbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            label,
            labelClass,
            wrapperClass,
            id,
            name,
            inForm,
            description,
            noBorder,
            inputClass,
            checkmarkClass,
            variant = "default",
            ...props
        },
        ref
    ) => {
        const hasDescription = Boolean(description);

        return (
            <div
                className={cn(
                    wrapperClass,
                    styles.checkboxWrapper,
                    styles[variant],
                    inForm && styles.inForm,
                    hasDescription && styles.hasDescription,
                    noBorder && styles.noBorder
                )}
            >
                <div className={styles.checkboxLabelWrapper}>
                    <label
                        role="button"
                        tabIndex={0}
                        htmlFor={id}
                        className={cn(
                            styles.checkboxLabel,
                            styles[`${variant}Label`]
                        )}
                    >
                        <input
                            className={cn(
                                styles.checkboxInput,
                                styles[`${variant}Input`],
                                inputClass
                            )}
                            type="checkbox"
                            id={id}
                            name={name}
                            ref={ref}
                            {...props}
                        />
                        <span
                            className={cn(
                                styles.checkmark,
                                styles[`${variant}Checkmark`],
                                checkmarkClass
                            )}
                        >
                            <CheckIcon />
                        </span>
                        {label && (
                            <span
                                className={cn(
                                    labelClass,
                                    styles.checkboxText,
                                    styles[`${variant}Text`]
                                )}
                            >
                                {label}
                            </span>
                        )}
                    </label>
                </div>
                {description && (
                    <span className={styles.description}>{description}</span>
                )}
            </div>
        );
    }
);

FormCheckbox.displayName = "FormCheckbox";

export { FormCheckbox };
